import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";

export default function IftarSummarize() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [speakingRow, setSpeakingRow] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [phaseLoading, setPhaseLoading] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(null);

  const revealLockRef = useRef(false);
  const revealLockTimerRef = useRef(null);
  const speakingRowRef = useRef(null);
  const doneRef = useRef(false); // mirrors done, readable inside intervals

  const lockReveal = () => {
    revealLockRef.current = true;
    if (revealLockTimerRef.current) clearTimeout(revealLockTimerRef.current);
    revealLockTimerRef.current = setTimeout(() => {
      revealLockRef.current = false;
    }, 1500);
  };

  // ─── Fetch current phase on mount ────────────────────────────────────────────
  useEffect(() => {
    const fetchPhase = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetPhaseStatus`);
        const data = res.data;
        const phase = Array.isArray(data) ? data[0]?.phase_status : data?.phase_status;
        setCurrentPhase(Number(phase));
      } catch (err) {
        console.error("Failed to fetch phase:", err);
      }
    };
    fetchPhase();
  }, []);

  // ─── Update phase and navigate ────────────────────────────────────────────────
  const handlePhaseChange = async (phaseNumber) => {
    setPhaseLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeUpdatePhaseStatus`, {
        phaseStatus: phaseNumber,
      });
      setCurrentPhase(phaseNumber);
    } catch (err) {
      console.error("Failed to update phase:", err);
    } finally {
      setPhaseLoading(false);
    }
  };

  // ─── Finish: load questions and mark done ────────────────────────────────────
  const finishAndLoad = useCallback(async () => {
    if (doneRef.current) return; // already finished
    doneRef.current = true;
    try {
      const summaryRes = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetSummarize`);
      if (Array.isArray(summaryRes.data) && summaryRes.data.length > 0) {
        setQuestions(summaryRes.data.map((row) => row.question));
      }
      lockReveal();
      setActiveIndex(null);
      setDone(true);
    } catch (err) {
      console.error("finishAndLoad error:", err);
      doneRef.current = false; // allow retry
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── On mount: if we came back from a reload, auto-load questions ─────────────
  useEffect(() => {
    const shouldReload = sessionStorage.getItem("iftarReloadQuestions");
    if (shouldReload === "1") {
      sessionStorage.removeItem("iftarReloadQuestions");
      handleLoadQuestions();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Background status watcher while loading ─────────────────────────────────
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(async () => {
      if (doneRef.current) { clearInterval(interval); return; }
      try {
        const res = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetStatus`);
        const data = res.data;
        const currentStatus = Array.isArray(data) ? data[0]?.status : data?.status;
        if (Number(currentStatus) === 1) {
          clearInterval(interval);
          finishAndLoad();
        }
      } catch {}
    }, 800);
    return () => clearInterval(interval);
  }, [loading, finishAndLoad]);

  // ─── Generate ────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    doneRef.current = false;
    setLoading(true);
    setDone(false);
    setError("");
    setQuestions([]);
    setActiveIndex(null);

    try {
      await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeUpdateStatusProcess`);

      const dbRes = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetQuestion`);
      const rawData = dbRes.data;

      const summarizeRes = await axios.post(
        `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeSummarize`,
        { rawData }
      );

      const qs = summarizeRes.data.questions;
      if (!qs || !Array.isArray(qs)) throw new Error("Invalid response from summarizer.");

      await Promise.all(
        qs.map((q) =>
          axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeInsertSummarize`, { question: q })
        )
      );

      await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeUpdateStatusDone`);
      await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeSetRevealRow`, { revealCount: 0 }).catch(() => {});
      await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeSetSpeakRow`, { rowIndex: null }).catch(() => {});

      await finishAndLoad();

    } catch (err) {
      console.error("handleGenerate error:", err);
      setTimeout(() => {
        if (!doneRef.current) {
          setError("Something went wrong. Please try again.");
          setLoading(false);
        }
      }, 3000);
      try {
        await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeUpdateStatusDone`);
      } catch {}
    }
  };

  // ─── Load existing ────────────────────────────────────────────────────────────
  const handleLoadQuestions = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetSummarize`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setQuestions(res.data.map((row) => row.question));
        setDone(true);
      }
      const revealRes = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetRevealRow`).catch(() => null);
      if (revealRes?.data) {
        const count = Array.isArray(revealRes.data) ? revealRes.data[0]?.revealCount : revealRes.data?.revealCount;
        const val = count !== undefined && count !== null ? Number(count) : 0;
        setActiveIndex(val === 0 ? null : val - 1);
      }
    } catch (err) {
      console.error("Load questions error:", err);
    }
  };

  // ─── Show a specific question on Dashboard ───────────────────────────────────
  const handleShowQuestion = async (index) => {
    const newRevealCount = activeIndex === index ? 0 : index + 1;
    const newActiveIndex = activeIndex === index ? null : index;

    lockReveal();
    setActiveIndex(newActiveIndex);

    try {
      await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeSetRevealRow`, {
        revealCount: newRevealCount,
      });
    } catch (err) {
      console.error("Set reveal row error:", err);
      lockReveal();
      setActiveIndex(activeIndex);
    }
  };

  // ─── Speak a question on Dashboard ───────────────────────────────────────────
  const handleSpeak = async (index) => {
    if (speakingRow !== null) return;
    setSpeakingRow(index);
    speakingRowRef.current = index;
    try {
      await axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeSetSpeakRow`, { rowIndex: index });
    } catch (err) {
      console.error("Set speak row error:", err);
      setSpeakingRow(null);
      speakingRowRef.current = null;
    }
  };

  // ─── Reset speak row on mount ─────────────────────────────────────────────────
  useEffect(() => {
    axios.post(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeSetSpeakRow`, { rowIndex: null }).catch(() => {});
  }, []);

  // ─── Poll speak row ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (questions.length === 0) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetSpeakRow`);
        const data = res.data;
        const rowIndex = Array.isArray(data) ? data[0]?.rowIndex : data?.rowIndex;
        const val = rowIndex !== undefined && rowIndex !== null ? Number(rowIndex) : null;

        const prev = speakingRowRef.current;

        if (prev !== null && val === null) {
          speakingRowRef.current = null;
          setSpeakingRow(null);
          revealLockRef.current = false;
          sessionStorage.setItem("iftarReloadQuestions", "1");
          window.location.reload();
          return;
        }

        speakingRowRef.current = val;
        setSpeakingRow(val);

        if (val === null) {
          revealLockRef.current = false;
        }
      } catch {}
    }, 600);
    return () => clearInterval(interval);
  }, [questions]);

  // ─── Poll reveal row ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (questions.length === 0) return;
    const interval = setInterval(async () => {
      if (revealLockRef.current) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetRevealRow`);
        const data = res.data;
        const count = Array.isArray(data) ? data[0]?.revealCount : data?.revealCount;
        const val = count !== undefined && count !== null ? Number(count) : 0;
        setActiveIndex(val === 0 ? null : val - 1);
      } catch {}
    }, 600);
    return () => clearInterval(interval);
  }, [questions]);

  // ─── Phase button config ──────────────────────────────────────────────────────
  const phaseButtons = [
    {
      phase: 0,
      label: "No Phase",
      icon: "○",
      desc: "Idle screen",
      color: "rgba(255,255,255,0.15)",
      activeColor: "rgba(255,255,255,0.85)",
    },
    {
      phase: 1,
      label: "Phase 1",
      icon: "◐",
      desc: "Polling",
      color: "rgba(100,200,255,0.25)",
      activeColor: "#64c8ff",
    },
    {
      phase: 2,
      label: "Phase 2",
      icon: "●",
      desc: "Live session",
      color: "rgba(232,200,74,0.25)",
      activeColor: "#e8c84a",
    },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #07111f 0%, #0a1628 60%, #061018 100%)",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "20px",
      padding: "32px 20px",
      boxSizing: "border-box",
    }}>

      {/* Header */}
      <div style={{ textAlign: "center" }}>
        <p style={{ margin: "0 0 6px", fontSize: "11px", letterSpacing: "3px", color: "rgba(255,255,255,0.3)", textTransform: "uppercase" }}>
          Iftar Exchange 2026
        </p>
        <h2 style={{ margin: 0, fontSize: "clamp(18px, 2.5vw, 26px)", fontWeight: "700", color: "#e8c84a" }}>
          AI Question Summarizer
        </h2>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={loading}
        style={{
          padding: "14px 36px",
          background: loading ? "rgba(232,200,74,0.15)" : "linear-gradient(135deg, #e8c84a 0%, #f0a500 100%)",
          border: loading ? "1px solid rgba(232,200,74,0.4)" : "none",
          borderRadius: "8px",
          color: loading ? "#e8c84a" : "#07111f",
          fontWeight: "700",
          fontSize: "14px",
          letterSpacing: "2px",
          textTransform: "uppercase",
          cursor: loading ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          boxShadow: loading ? "none" : "0 4px 20px rgba(232,200,74,0.35)",
        }}
      >
        {loading ? (
          <><span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> Processing with AI...</>
        ) : (
          <>✦ Generate Summary</>
        )}
      </button>

      {!done && !loading && (
        <button
          onClick={handleLoadQuestions}
          style={{
            padding: "10px 24px",
            background: "transparent",
            border: "1px solid rgba(232,200,74,0.3)",
            borderRadius: "8px",
            color: "rgba(232,200,74,0.7)",
            fontWeight: "600",
            fontSize: "12px",
            letterSpacing: "2px",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(232,200,74,0.7)"}
          onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(232,200,74,0.3)"}
        >
          Load Existing Questions
        </button>
      )}

      {done && !loading && !error && (
        <p style={{ margin: 0, fontSize: "13px", color: "#6ee7b7", letterSpacing: "1px" }}>
          ✓ {questions.length} questions ready — select one to display.
        </p>
      )}
      {error && (
        <p style={{
          margin: 0, fontSize: "13px", color: "#ff6b6b",
          background: "rgba(255,80,80,0.08)", padding: "10px 18px",
          borderRadius: "6px", border: "1px solid rgba(255,80,80,0.25)",
        }}>{error}</p>
      )}

      {done && !loading && questions.length > 0 && (
        <div style={{
          width: "100%",
          maxWidth: "640px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          marginTop: "4px",
          animation: "fadeIn 0.5s ease",
        }}>
          <p style={{
            margin: "0 0 4px",
            fontSize: "10px",
            letterSpacing: "3px",
            color: "rgba(255,255,255,0.25)",
            textTransform: "uppercase",
            textAlign: "center",
          }}>Presenter Controls · Click to show on screen</p>

          {questions.map((q, i) => {
            const isActive = activeIndex === i;
            const isSpeaking = speakingRow === i;

            return (
              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                <button
                  onClick={() => handleShowQuestion(i)}
                  style={{
                    flex: 1,
                    padding: "13px 16px",
                    background: isActive ? "rgba(232,200,74,0.12)" : "rgba(10, 25, 50, 0.6)",
                    border: isActive ? "1px solid rgba(232,200,74,0.6)" : "1px solid rgba(30, 60, 100, 0.6)",
                    borderRadius: "8px",
                    color: isActive ? "#e8c84a" : "rgba(255,255,255,0.7)",
                    fontWeight: "600",
                    fontSize: "13px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    textAlign: "left",
                    transition: "all 0.2s ease",
                    boxShadow: isActive ? "0 0 16px rgba(232,200,74,0.12)" : "none",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = "rgba(232,200,74,0.35)";
                      e.currentTarget.style.background = "rgba(232,200,74,0.05)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = "rgba(30, 60, 100, 0.6)";
                      e.currentTarget.style.background = "rgba(10, 25, 50, 0.6)";
                    }
                  }}
                >
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0, width: "3px",
                    background: isActive
                      ? "linear-gradient(180deg, #f7d75a 0%, #e8a000 100%)"
                      : "rgba(255,255,255,0.06)",
                    borderRadius: "8px 0 0 8px",
                    transition: "all 0.2s ease",
                    boxShadow: isActive ? "0 0 8px rgba(232,200,74,0.4)" : "none",
                  }} />
                  <span style={{
                    minWidth: "28px",
                    fontSize: "20px",
                    fontWeight: "800",
                    color: isActive ? "#e8c84a" : "rgba(255,255,255,0.18)",
                    flexShrink: 0,
                    transition: "color 0.2s",
                  }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                    color: isActive ? "rgba(232,200,74,0.85)" : "rgba(255,255,255,0.55)",
                    transition: "color 0.2s",
                  }}>
                    {q}
                  </span>
                  <span style={{
                    flexShrink: 0,
                    fontSize: "11px",
                    fontWeight: "700",
                    letterSpacing: "1.5px",
                    color: isActive ? "#e8c84a" : "rgba(255,255,255,0.2)",
                    textTransform: "uppercase",
                    transition: "color 0.2s",
                  }}>
                    {isActive ? "ON SCREEN" : "SHOW"}
                  </span>
                </button>

                <button
                  onClick={() => handleSpeak(i)}
                  disabled={speakingRow !== null || !isActive}
                  title={!isActive ? "Show question first" : isSpeaking ? "Speaking..." : "Read aloud"}
                  style={{
                    flexShrink: 0,
                    width: "48px",
                    borderRadius: "8px",
                    border: isSpeaking
                      ? "1px solid rgba(232,200,74,0.6)"
                      : isActive
                        ? "1px solid rgba(232,200,74,0.25)"
                        : "1px solid rgba(255,255,255,0.06)",
                    background: isSpeaking
                      ? "rgba(232,200,74,0.15)"
                      : isActive
                        ? "rgba(232,200,74,0.06)"
                        : "rgba(255,255,255,0.03)",
                    cursor: (speakingRow !== null || !isActive) ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    transition: "all 0.2s ease",
                    opacity: !isActive ? 0.3 : 1,
                  }}
                >
                  {isSpeaking ? (
                    <span style={{ animation: "pulse 1s ease-in-out infinite" }}>🔊</span>
                  ) : (
                    <span>🔈</span>
                  )}
                </button>
              </div>
            );
          })}

          {/* Now on screen status */}
          <div style={{
            marginTop: "6px",
            padding: "10px 16px",
            borderRadius: "8px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <span style={{ fontSize: "11px", letterSpacing: "2px", color: "rgba(255,255,255,0.2)", textTransform: "uppercase" }}>
              Now on screen
            </span>
            <span style={{
              fontSize: "13px",
              fontWeight: "700",
              color: activeIndex !== null ? "#e8c84a" : "rgba(255,255,255,0.2)",
              letterSpacing: "0.5px",
            }}>
              {activeIndex !== null ? `Question ${String(activeIndex + 1).padStart(2, "0")}` : "— Nothing —"}
            </span>
          </div>
        </div>
      )}

      {/* ─── Phase Control Panel ──────────────────────────────────────────────── */}
      <div style={{
        width: "100%",
        maxWidth: "640px",
        marginTop: "8px",
        padding: "18px 20px",
        borderRadius: "10px",
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.07)",
        boxSizing: "border-box",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}>
          <p style={{
            margin: 0,
            fontSize: "10px",
            letterSpacing: "3px",
            color: "rgba(255,255,255,0.25)",
            textTransform: "uppercase",
          }}>
            Phase Control
          </p>
          {currentPhase !== null && (
            <span style={{
              fontSize: "10px",
              letterSpacing: "1.5px",
              color: "rgba(255,255,255,0.2)",
              textTransform: "uppercase",
            }}>
              Current:{" "}
              <span style={{
                color: currentPhase === 0 ? "rgba(255,255,255,0.5)"
                  : currentPhase === 1 ? "#64c8ff"
                  : "#e8c84a",
                fontWeight: "700",
              }}>
                {currentPhase === 0 ? "No Phase" : `Phase ${currentPhase}`}
              </span>
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          {phaseButtons.map(({ phase, label, icon, desc, color, activeColor }) => {
            const isCurrentPhase = currentPhase === phase;
            return (
              <button
                key={phase}
                onClick={() => handlePhaseChange(phase)}
                disabled={phaseLoading || isCurrentPhase}
                title={isCurrentPhase ? "Already active" : `Switch to ${label}`}
                style={{
                  flex: 1,
                  padding: "12px 10px",
                  borderRadius: "8px",
                  border: isCurrentPhase
                    ? `1px solid ${activeColor}`
                    : "1px solid rgba(255,255,255,0.08)",
                  background: isCurrentPhase
                    ? color
                    : "rgba(255,255,255,0.02)",
                  cursor: phaseLoading || isCurrentPhase ? "not-allowed" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "5px",
                  transition: "all 0.2s ease",
                  opacity: phaseLoading && !isCurrentPhase ? 0.5 : 1,
                  boxShadow: isCurrentPhase ? `0 0 14px ${color}` : "none",
                }}
                onMouseEnter={e => {
                  if (!isCurrentPhase && !phaseLoading) {
                    e.currentTarget.style.borderColor = activeColor;
                    e.currentTarget.style.background = color;
                  }
                }}
                onMouseLeave={e => {
                  if (!isCurrentPhase && !phaseLoading) {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                  }
                }}
              >
                <span style={{
                  fontSize: "18px",
                  color: isCurrentPhase ? activeColor : "rgba(255,255,255,0.25)",
                  transition: "color 0.2s",
                }}>
                  {phaseLoading && isCurrentPhase ? (
                    <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span>
                  ) : icon}
                </span>
                <span style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: isCurrentPhase ? activeColor : "rgba(255,255,255,0.35)",
                  transition: "color 0.2s",
                }}>
                  {label}
                </span>
                <span style={{
                  fontSize: "9px",
                  letterSpacing: "1px",
                  color: isCurrentPhase ? activeColor : "rgba(255,255,255,0.18)",
                  textTransform: "uppercase",
                  opacity: 0.8,
                }}>
                  {desc}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}